import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	Legend,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	Line,
	LineChart,
	Area,
	AreaChart,
	LabelList,
} from 'recharts';
import TableLoading from '../../../components/TableLoading';

import { analyticsAPI } from '../../../services/constants';
import { formatDuration, request } from '../../../services/utilities';
import { notifyError } from '../../../services/notify';
import moment from 'moment';
import SlidingPane from '../../../components/SlidingPane';
import { Card, Col, Row, Statistic } from 'antd';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const StaffRadiologyOverview = ({ selectedStaff }) => {
	const [loading, setLoading] = useState({
		trend: true,
		tatTop: true,
		tatBottom: true,
		top10Count: true,
		bottom10Count: true,
		kpi: true,
	});
	const [filters, setFilters] = useState({
		trend: { dateFilter: 'past-month' },
		tatTop: { dateFilter: 'past-month' },
		tatBottom: { dateFilter: 'past-month' },
		top10Count: { dateFilter: 'past-month' },
		bottom10Count: { dateFilter: 'past-month' },
		requestMetrics: { dateFilter: 'past-month' },
	});
	const prevFiltersRef = useRef(null);
	const [top10Count, setTop10Count] = useState([]);
	const [bottom10Count, setBottom10Count] = useState([]);
	const [radiologyTrend, setRadiologyTrend] = useState([]);
	const [tatTop10, setTatTop10] = useState([]);
	const [tatBottom10, setTatBottom10] = useState([]);
	const [modal, setModal] = useState(false);

	const [requestMetrics, setRequestMetrics] = useState({
		totalRequests: '0',
		notReceived: null,
		received: null,
		notSubstituted: null,
		substituted: null,
		dateScheduled: null,
		dateNotScheduled: null,
		notCancelled: null,
		cancelled: null,
		notApproved: null,
		approved: null,
		notFilled: null,
		filled: null,
		deleted: null,
		cancelledOnly: null,
		totalCancelled: 0,
		deletedCount: 0,
		cancelledOnlyCount: 0,
	});

	const [modalAlt, setModalAlt] = useState(false);

	const COLORS = [
		'#0088FE', // blue
		'#00C49F', // green
		'#FFBB28', // yellow
		'#FF8042', // orange
		'#8884d8', // purple
		'#FF4444', // red
		'#22BB33', // dark green
	];

	const onFilterChange = (filterKey, value) => {
		setFilters(prev => ({
			...prev,
			[filterKey]: {
				...(prev[filterKey] || {}),
				dateFilter: value,
			},
		}));
	};

	const TimeRangeDropdown = ({ chartId, value }) => {
		return (
			<div className="mb-4                                                                                                                                                                                  ">
				<form className="form-inline justify-content-sm-end">
					<select
						className="form-control form-control-sm rounded"
						onChange={e => {
							onFilterChange(chartId, e.target.value);
						}}
						value={value}
					>
						<option value="today">Today</option>
						<option value="past-week">Past Week</option>
						<option value="past-month">Past 30 Days</option>
						<option value="last-month">Last Month</option>
						<option value="past-quarter">Past Quarter</option>
						<option value="past-six-months">Past Six Months</option>
						<option value="past-year">Past Year</option>
						<option value="last-year">Last Year</option>
						<option value="past-three-years">Past Three Years</option>
						<option value="all-time">All Time</option>
					</select>
				</form>
			</div>
		);
	};

	// fetch Top10 Count based on current filter
	const fetchTop10Count = useCallback(async () => {
		const url = `${analyticsAPI}/radiology-count-type?dateFilter=${filters?.top10Count?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs?.result || [];
	}, [filters?.top10Count?.dateFilter]);

	// Fetch Bottom 10 Count based on current filter
	const fetchBottom10Count = useCallback(async () => {
		const url = `${analyticsAPI}/radiology-count-type?dateFilter=${filters?.bottom10Count?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs?.result || [];
	}, [filters?.bottom10Count?.dateFilter]);

	// Fetch Radiology Trends based on current filter
	const fetchRadiologyTrends = useCallback(async () => {
		const url = `${analyticsAPI}/radiology-trend?dateFilter=${filters?.trend?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs?.result || [];
	}, [filters?.trend?.dateFilter]);

	// Fetch total request count based on current filter
	const fetchTatTop10 = useCallback(async () => {
		const url = `${analyticsAPI}/radiology-turn?dateFilter=${filters?.tatTop?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);

		// console.log(rs?.data?.top10, 'top_');
		return rs?.data?.result || [];
	}, [filters?.tatTop?.dateFilter]);

	// fetch Tat Bottom 10 based on current filter
	const fetchTatBottom10 = useCallback(async () => {
		const url = `${analyticsAPI}/radiology-turn?dateFilter=${filters?.tatBottom?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs?.data?.result || [];
	}, [filters?.tatBottom?.dateFilter]);

	const fetchRequestMetrics = useCallback(async () => {
		const url = `${analyticsAPI}/patient-request-metrics?dateFilter=${filters?.requestMetrics?.dateFilter}&requestType=scans&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs?.result || {};
	}, [filters?.requestMetrics?.dateFilter]);

	//
	useEffect(() => {
		const prevFilters = prevFiltersRef.current;

		// Handle requestMetrics separately
		const currentMetricsFilter = filters?.requestMetrics?.dateFilter;
		const prevMetricsFilter = prevFilters?.requestMetrics?.dateFilter;

		if (currentMetricsFilter && (!prevMetricsFilter || currentMetricsFilter !== prevMetricsFilter)) {
			setLoading(prev => ({ ...prev, kpi: true }));

			fetchRequestMetrics()
				.then(data => setRequestMetrics(data))
				.catch(() => notifyError('Error fetching request metrics'))
				.finally(() => setLoading(prev => ({ ...prev, kpi: false })));
		}

		const fetchMap = {
			trend: fetchRadiologyTrends,
			tatTop: fetchTatTop10,
			tatBottom: fetchTatBottom10,
			top10Count: fetchTop10Count,
			bottom10Count: fetchBottom10Count,
		};

		Object.entries(fetchMap).forEach(([key, fetchFn]) => {
			const currentFilter = filters?.[key]?.dateFilter;
			const prevFilter = prevFilters?.[key]?.dateFilter;

			if (currentFilter && (!prevFilter || currentFilter !== prevFilter)) {
				setLoading(prev => ({ ...prev, [key]: true }));

				fetchFn()
					.then(data => {
						if (key === 'trend') {
							setRadiologyTrend(data);
						} else if (key === 'tatTop') {
							setTatTop10(data);
						} else if (key === 'tatBottom') {
							setTatBottom10(data);
						} else if (key === 'top10Count') {
							setTop10Count(data);
						} else if (key === 'bottom10Count') {
							setBottom10Count(data);
						}
					})
					.catch(error => notifyError('Error fetching request count'))
					.finally(() => setLoading(prev => ({ ...prev, [key]: false })));
			}
		});

		// Update ref after processing
		prevFiltersRef.current = filters;
	}, [filters]);

	//All chart data(s)
	const bottom10Chart = bottom10Count?.map(item => ({
		name: item?.name || 'Unknown',
		count: parseInt(item?.count, 10),
	}));

	const trendChart = radiologyTrend?.map(item => ({
		name:
			item?.date ||
			item?.year ||
			(filters?.trend?.dateFilter == 'past-three-years' && moment(item?.month).format('MMMM,YY')) ||
			(item?.month && moment(item?.month).format('MMM')) ||
			item?.day,
		count: parseInt(item?.count, 10),
	}));

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const renderCustomLabelAlt2 = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{/* {value?.toLocaleString() + 'mins'} */}
				{formatDuration(value)}
			</text>
		);
	};

	const CustomToolTipAlt = ({ active, payload, label }) => {
		if (!active || !payload?.length) return null;

		const data = payload[0]?.payload;

		return (
			<div className="custom-tooltip" style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: 10 }}>
				<p>
					<strong>{String(label).charAt(0).toUpperCase() + String(label).slice(1)}</strong>
				</p>
				<p>Average Turn Around Time: {formatDuration(data?.avgTATMinutes)}</p>
			</div>
		);
	};

	const displayTop10Count = top10Count?.map(item => ({
		name: item?.name || 'Unknown',
		count: parseInt(item?.count, 10),
	}));

	const MetricCard = ({ loading, value, label, total, color = 'primary' }) => {
		const percentage = value != null && total > 0 ? ((value / total) * 100).toFixed(1) : 0;
		return (
			<div className="col-sm-3">
				<a className="element-box el-tablo" href="#">
					<div className="label">{label}</div>
					<div className={`value`}>{loading ? <TableLoading /> : value != null ? Number(value)?.toLocaleString() : '0'}</div>
					<div className={`trending ${percentage > 0 ? 'trending-up-basic' : 'trending-down-basic'}`}>
						<span>{percentage > 0 ? percentage : '0'}%</span>
						{percentage > 0 ? <i className="os-icon os-icon-arrow-up2"></i> : <i className="os-icon os-icon-arrow-down"></i>}
					</div>
				</a>
			</div>
		);
	};

	const metricsConfig = [
		{ key: 'totalRequests', label: 'Total Requests' },
		{ key: 'cancelledOnly', label: 'Cancelled Only' },
		{ key: 'cancelled', label: 'Cancelled' },
		{ key: 'approved', label: 'Approved' },
		{ key: 'notApproved', label: 'Not Approved' },
		{ key: 'filled', label: 'Filled' },
		{ key: 'notFilled', label: 'Not Filled' },
		{ key: 'substituted', label: 'Substituted' },
		{ key: 'notSubstituted', label: 'Not Substituted' },
		{ key: 'received', label: 'Received' },
		{ key: 'notReceived', label: 'Not Received' },
		{ key: 'deleted', label: 'Deleted' },
	];

	const totalValue = Number(requestMetrics?.totalRequests) || 0;

	return (
		<div className="content-i">
			<div className="content-box">
				<div className="row">
					{/* Overview Metrics */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Key Performance Indicators</h6>
								<div className="form-inline justify-content-end mb-4">
									<TimeRangeDropdown chartId="requestMetrics" value={filters.requestMetrics.dateFilter} />
								</div>
							</div>
							<p className="chart-description mb-3 p-2 card">Summary metrics showing overall radiology activity and engagement levels.</p>
							<div className="element-content">
								<div className="row">
									{loading.kpi ? (
										<TableLoading />
									) : (
										<div className="row">
											{metricsConfig.map(({ key, label, color }) => {
												const value = requestMetrics?.[key];
												return <MetricCard key={key} label={label} color={color} value={value} loading={false} total={totalValue} />;
											})}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Radiology Trends*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> Radiology Trends </h6>

							<TimeRangeDropdown chartId="trend" value={filters?.trend?.dateFilter} />
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Analyzes trends in radiology test performance, providing a comprehensive overview of imaging procedure patterns and shifts
								based on selected date filters
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.trend ? (
												<TableLoading />
											) : trendChart.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<AreaChart data={trendChart} margin={{ top: 20, right: 20, left: 20, bottom: 50 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey={'name'}
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={filters?.trend?.dateFilter === 'past-quarter' ? 2 : 0}
																tickFormatter={value => {
																	const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Legend wrapperStyle={{ marginBottom: '-50px' }} name="readings" />
															<Area
																type="monotone"
																dataKey="count"
																stroke="#10B981"
																fill="#10B981"
																strokeWidth={2}
																dot={{ r: 4 }}
																activeDot={{ r: 6 }}
																// name='Count'
															>
																<LabelList
																	dataKey="count"
																	content={renderCustomLabelAlt}
																	position="top"
																	style={{ fontSize: '12px', fill: '#333' }}
																/>
															</Area>
														</AreaChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					{/* Top 10 Count */}
					<div className="col-sm-12 col-lg-6">
						<div className="element-wrapper">
							<h6 className="element-header"> Radiology Type by Count(Top 10) </h6>

							<TimeRangeDropdown chartId="top10Count" value={filters?.top10Count?.dateFilter} />

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Displays the top 10 radiology test types by count, highlighting the most frequently performed tests.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.top10Count ? (
												<TableLoading />
											) : top10Count.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer width="100%" height={400}>
														<BarChart data={displayTop10Count?.slice(0, 10)} margin={{ top: 30, right: 20, left: 20, bottom: 50 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey="name"
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={0}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Bar dataKey="count" name="Count">
																{top10Count.map((entry, index) => (
																	<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																))}
																<LabelList dataKey="count" name="count" position="top" style={{ fontSize: '12px', fill: '#333' }} />
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Bottom 10 Count */}
					<div className="col-sm-12 col-lg-6">
						<div className="element-wrapper">
							<h6 className="element-header"> Radiology Type by Count(Bottom 10) </h6>
							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModal(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdown chartId="bottom10Count" value={filters?.bottom10Count?.dateFilter} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Displays the bottom 10 radiology test types by count, highlighting the least frequently performed tests.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.bottom10Count ? (
												<TableLoading />
											) : bottom10Chart.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer width="100%" height={400}>
														<BarChart data={bottom10Chart?.slice(-10)} margin={{ top: 30, right: 20, left: 20, bottom: 40 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey="name"
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={0}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Bar dataKey="count" name="Count">
																{top10Count.map((entry, index) => (
																	<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																))}
																<LabelList
																	dataKey="count"
																	content={renderCustomLabelAlt}
																	name="count"
																	position="top"
																	style={{ fontSize: '12px', fill: '#333' }}
																/>
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>

						<SlidingPane isOpen={modal}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModal(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdown chartId="bottom10Count" value={filters?.bottom10Count?.dateFilter} />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">All Radiology Type by Count.</h6>

											<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												{/* <TableLoading /> */}

												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total" value={bottom10Chart?.length} suffix="" />
															<small className="text-muted">Total Radiology Types</small>
														</Card>
													</Col>
													{/* <Col span={8}>
														<Card>
															<Statistic
																title="Total Revenue"
																value={transSummary?.totalRevenueFormatted} suffix=""
															/>

															<small className="text-muted">Cumulative Revenue From --- to --</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic
																title="Total Transactions"
																value={transSummary?.totalTransactions} suffix=""
															/>
															<Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/>
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col> */}
													{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										This chart Displays the all radiology test types by count, highlighting the tests performed tests.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loading.bottom10Count ? (
															<TableLoading />
														) : bottom10Chart.length > 0 ? (
															<div style={{ width: '100%' }}>
																{bottom10Chart.length > 30 ? (
																	<ResponsiveContainer width="100%" height={bottom10Chart?.length * 40}>
																		<BarChart
																			data={bottom10Chart}
																			interval={0}
																			layout="vertical"
																			margin={{ top: 30, right: 20, left: 100, bottom: 40 }} // left margin increased for labels
																		>
																			<CartesianGrid strokeDasharray="3 3" />

																			{/* Value axis (numbers) */}
																			<XAxis type="number" domain={[0, 'auto']} />

																			{/* Category axis (names) */}
																			<YAxis
																				dataKey="name"
																				type="category"
																				interval={0}
																				width={100} // adjust so labels fit
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>

																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>

																			<Bar dataKey="count" name="Count">
																				{bottom10Chart.map((entry, index) => (
																					<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																				))}
																				<LabelList
																					dataKey="count"
																					position="right" // position labels at the end of the bars
																					style={{ fontSize: '12px', fill: '#333' }}
																				/>
																			</Bar>
																		</BarChart>
																	</ResponsiveContainer>
																) : (
																	<ResponsiveContainer width="100%" height={400}>
																		<BarChart data={bottom10Chart} margin={{ top: 30, right: 20, left: 20, bottom: 40 }}>
																			<CartesianGrid strokeDasharray="3 3" />
																			<XAxis
																				dataKey="name"
																				angle={-45}
																				textAnchor="end"
																				fontSize={12}
																				interval={0}
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>
																			<YAxis domain={[0, 'auto']} />
																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>
																			<Bar dataKey="count" name="Count">
																				{top10Count.map((entry, index) => (
																					<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																				))}
																				<LabelList
																					dataKey="count"
																					content={renderCustomLabelAlt}
																					name="count"
																					position="top"
																					style={{ fontSize: '12px', fill: '#333' }}
																				/>
																			</Bar>
																		</BarChart>
																	</ResponsiveContainer>
																)}
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/*Top 10 */}
					<div className="col-sm-12 col-lg-">
						<div className="element-wrapper">
							<h6 className="element-header"> Turn Around Time (Top 10) </h6>

							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdown chartId="tatTop" value={filters?.tatTop?.dateFilter} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Presents the top 10 tests with the highest turn around times, offering insights into processing durations and potential
								efficiency areas based on selected date filters
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.tatTop ? (
												<TableLoading />
											) : tatTop10.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<LineChart
															data={tatTop10?.slice(0, 10)}
															margin={{
																top: 30,
																right: 30,
																left: 20,
																bottom: 50,
															}}
														>
															<CartesianGrid strokeDasharray="3 3" />

															<XAxis
																dataKey="testName"
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={0}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip content={CustomToolTipAlt} />
															<Line
																type="monotone"
																dataKey="avgTATMinutes"
																stroke="#0088FE"
																strokeWidth={2}
																dot={{ r: 4 }}
																activeDot={{ r: 6 }}
															>
																<LabelList
																	dataKey="avgTATMinutes"
																	name="value"
																	position="top"
																	content={renderCustomLabelAlt2}
																	style={{ fontSize: '12px', fill: '#0088FE' }}
																/>
															</Line>
														</LineChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Bottom 10 */}
					<div className="col-sm-12 col-lg-">
						<div className="element-wrapper">
							<h6 className="element-header"> Turn Around Time (Bottom 10) </h6>

							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdown chartId="tatBottom" value={filters?.tatBottom?.dateFilter} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Highlights the bottom 10 tests with the lowest turn around times, showcasing the most efficient processing durations and
								performance benchmarks based on selected date filters
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.tatBottom ? (
												<TableLoading />
											) : tatBottom10.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<LineChart
															data={tatBottom10?.slice(-10)}
															margin={{
																top: 20,
																right: 30,
																left: 20,
																bottom: 50,
															}}
														>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey="testName"
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={0}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip content={CustomToolTipAlt} />
															<Line
																type="monotone"
																dataKey="avgTATMinutes"
																stroke="#0088FE"
																strokeWidth={2}
																dot={{ r: 4 }}
																activeDot={{ r: 6 }}
															>
																<LabelList
																	dataKey="avgTATMinutes"
																	name="value"
																	position="top"
																	content={renderCustomLabelAlt2}
																	style={{ fontSize: '12px', fill: '#0088FE' }}
																/>
															</Line>
														</LineChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>

								<SlidingPane isOpen={modalAlt}>
									<div className=" content-box">
										<div className=" d-flex justify-content-end w-100">
											<button className="btn btn-danger float-end" onClick={() => setModalAlt(false)}>
												&times;
											</button>
										</div>

										<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
											<TimeRangeDropdown chartId="tatBottom" value={filters?.tatBottom?.dateFilter} />
										</div>
										<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
											<div className="col-sm-12">
												<div className="element-wrapper">
													<h6 className="element-header"> Turn Around Time </h6>

													<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
														High-level metrics showing overall metrics of readings over time.
													</p>
													<div className="element-box">
														{/* <TableLoading /> */}

														<Row gutter={16}>
															<Col span={8}>
																<Card>
																	<Statistic title="Total Tests (TAT)" value={tatBottom10?.length} suffix="" />
																	<small className="text-muted">Total test Turn Around Time </small>
																</Card>
															</Col>
															{/* <Col span={8}>
																<Card>
																	<Statistic
																		title="Total Revenue"
																		value={transSummary?.totalRevenueFormatted} suffix=""
																	/>

																	<small className="text-muted">Cumulative Revenue From --- to --</small>
																</Card>
															</Col>
															<Col span={8}>
																<Card>
																	<Statistic
																		title="Total Transactions"
																		value={transSummary?.totalTransactions} suffix=""
																	/>
																	<Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/>
																	<small className="text-muted">Cumulative Transactions From --- to --</small>
																</Card>
															</Col> */}
															{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
														</Row>
													</div>
												</div>
											</div>
										</div>

										<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
											<p className="chart-description card p-2">
												Highlights test with the their turn around times, showcasing the efficient processing durations and performance
												benchmarks based on selected date filters
											</p>
										</div>

										<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
											<div className=" card w-100 ">
												<div className="row">
													<div className="col-md-12 mb-4">
														<div className="card h-100">
															<div className="card-body">
																{loading.tatBottom ? (
																	<TableLoading />
																) : tatBottom10.length > 0 ? (
																	<div style={{ width: '100%', height: 'auto' }}>
																		{tatBottom10.length > 30 ? (
																			<ResponsiveContainer width="100%" height={tatBottom10?.length * 30}>
																				<LineChart
																					data={tatBottom10}
																					layout="vertical"
																					margin={{
																						top: 20,
																						right: 30,
																						left: 100, // extra space for labels
																						bottom: 50,
																					}}
																				>
																					<CartesianGrid strokeDasharray="3 3" />

																					{/* Value axis (numbers) */}
																					<XAxis type="number" domain={[0, 'auto']} />

																					{/* Category axis (test names) */}
																					<YAxis
																						dataKey="testName"
																						type="category"
																						width={120} // adjust to fit labels
																						tickFormatter={value => {
																							const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																							const capitalized = truncated
																								.toLowerCase()
																								.split(' ')
																								.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																								.join(' ');
																							return capitalized;
																						}}
																					/>

																					<Tooltip content={CustomToolTipAlt} />

																					<Line
																						type="monotone"
																						dataKey="avgTATMinutes"
																						stroke="#0088FE"
																						strokeWidth={2}
																						dot={{ r: 4 }}
																						activeDot={{ r: 6 }}
																					>
																						<LabelList
																							dataKey="avgTATMinutes"
																							position="right" // put values at the end of lines
																							content={renderCustomLabelAlt2}
																							style={{ fontSize: '12px', fill: '#0088FE' }}
																						/>
																					</Line>
																				</LineChart>
																			</ResponsiveContainer>
																		) : (
																			<ResponsiveContainer>
																				<LineChart
																					data={tatBottom10}
																					margin={{
																						top: 20,
																						right: 30,
																						left: 20,
																						bottom: 50,
																					}}
																				>
																					<CartesianGrid strokeDasharray="3 3" />
																					<XAxis
																						dataKey="testName"
																						angle={-45}
																						textAnchor="end"
																						fontSize={12}
																						interval={0}
																						tickFormatter={value => {
																							const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																							const capitalized = truncated
																								.toLowerCase()
																								.split(' ')
																								.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																								.join(' ');
																							return capitalized;
																						}}
																					/>
																					<YAxis domain={[0, 'auto']} />
																					<Tooltip content={CustomToolTipAlt} />
																					<Line
																						type="monotone"
																						dataKey="avgTATMinutes"
																						stroke="#0088FE"
																						strokeWidth={2}
																						dot={{ r: 4 }}
																						activeDot={{ r: 6 }}
																					>
																						<LabelList
																							dataKey="avgTATMinutes"
																							name="value"
																							position="top"
																							content={renderCustomLabelAlt2}
																							style={{ fontSize: '12px', fill: '#0088FE' }}
																						/>
																					</Line>
																				</LineChart>
																			</ResponsiveContainer>
																		)}
																	</div>
																) : (
																	<div className="text-center fs-bold">No Record Found!</div>
																)}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</SlidingPane>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default StaffRadiologyOverview;
